import { useEffect, useMemo, useState } from "react";
import Table from "../../common/Table";
import { EyeIcon, PrinterIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import ViewLegislationModal from "../modals/ViewLegislationModal";
import { legislationsAPI } from "../../../services/api";
import toast from "react-hot-toast";
import DateTimeCell from "../../common/DateTimeCell";
import { useQuery } from "react-query";
import LegislationFileViewer from "../modals/LegislationFileViewer";
import { useFetch, useSubmission } from "../../../hooks/useCallApi";

function HistoryArchivesTable({ search, startDate, endDate, header, activeTab }) {

  const [selectedItem, setSelectedItem] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreItem, setRestoreItem] = useState(null);

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  })

  const params = useMemo(() => ({
    per_page: pagination.perPage,
    page: pagination.currentPage,
    search: search || undefined,
    archived_from: startDate || undefined,
    archived_to: endDate || undefined,
    section: 'history_archives',
  }), [pagination, search, startDate, endDate])

  const { data: historyData, isLoading, refetch } = useFetch(
    ['history-archives', params],
    api => api.admin.legislation.getTabView(params)
  )

  const { mutateAsync: restoreLegislation } = useSubmission(
    ['restore-legislation'],
    (api, id) => api.admin.legislation.restoreArchivedLegislation(id)
  );

  const { mutateAsync: printLegislation } = useSubmission(
    ['print-legislation'],
    (api, id, args) => api.admin.legislation.getLegislation(id, args)
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, currentPage: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const tableData = Array.isArray(historyData?.data?.data)
    ? historyData?.data?.data
    : []

  useEffect(() => {
    if (activeTab === 'History & Archives') refetch()
  }, [activeTab, refetch])

  const iframePrintLegislation = (item) => {
    if (!item?.legislative_file?.url) return alert('No file to print')

    const fileUrl = item.legislative_file.url
    const extension =
      item.legislative_file.name?.split('.').pop()?.toLowerCase() ||
      fileUrl.split('.').pop()?.toLowerCase()

    if (extension === 'pdf') {
      let printFrame = document.getElementById('legislation-print-iframe')
      if (!printFrame) {
        printFrame = document.createElement('iframe')
        printFrame.id = 'legislation-print-iframe'
        printFrame.style.position = 'fixed'
        printFrame.style.width = '0'
        printFrame.style.height = '0'
        printFrame.style.border = 'none'
        printFrame.style.visibility = 'hidden'
        document.body.appendChild(printFrame)
      }

      printFrame.src = fileUrl
      printFrame.onload = () => {
        printFrame.contentWindow.focus()
        printFrame.contentWindow.print()
      }
      return
    }

    if (extension === 'doc' || extension === 'docx') {
      const win = window.open(fileUrl, '_blank')
      if (!win) return alert('Pop-up blocked')
      win.focus()
    }
  }

  const handlePageChange = (page, perPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }))
  }

  const handleView = (item) => {
    setSelectedItem(item);
    setOpenViewModal(true);
  };

  const handlePrint = async (item) => {
    if (!item?.legislative_document_id) return toast.error("Invalid document ID");

    try {
      toast.loading("Loading document...", { id: "print" });
      const res = await printLegislation(item.legislative_document_id);

      const documentData = res.data?.data;
      if (!documentData) {
        toast.error("Document not found", { id: "print" });
        return;
      }

      setSelectedItem(documentData);
      setIsPrintModalOpen(true);
      toast.dismiss("print");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load document", { id: "print" });
    }
  };

  const handleRestore = (item) => {
    setRestoreItem(item);
    setRestoreModalOpen(true);
  };

  const confirmRestore = async () => {
    if (!restoreItem?.legislative_document_id) {
      toast.error("Invalid legislation");
      return;
    }

    try {
      toast.loading("Restoring...", { id: "restore" });

      await restoreLegislation(restoreItem.legislative_document_id);

      toast.success("Legislation restored!", { id: "restore" });

      setRestoreModalOpen(false);
      setRestoreItem(null);

      refetch();

    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to restore legislation",
        { id: "restore" }
      );
    }
  };


  const columns = [
    {
      key: "legislation_number",
      label: "Legislation Number",
      render: (_, item) =>
        <span className="text-sm font-medium text-gray-900">{item.legislation_number}</span>
    },
    {
      key: "title",
      label: "Title",
      render: (_, item) =>
        <div className="truncate max-w-[16rem] text-sm" title={item.title}>{item.title}
        </div>
    },
    {
      key: "action_description",
      label: "Action Description",
      render: (_, item) =>
        <div className="truncate max-w-[16rem] text-sm" title={item.action_description}>{item.action_description}
        </div>
    },
    {
      key: "remarks",
      label: "Remarks", render: (_, item) =>
        <div className="truncate max-w-[16rem] text-sm" title={item.remarks}>{item.remarks}</div>
    },
    {
      key: "archived_date",
      label: "Archived Date", render: (_, item) =>

        <span className="text-sm text-gray-500"><DateTimeCell value={item.archived_date} /></span>
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, item) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(item)}
            className='text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110'
            title='View Details'
          >
            <EyeIcon className='h-4 w-4' />
          </button>
          <button
            onClick={() => handlePrint(item)}
            className='text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50 transition-all duration-200 hover:scale-110'
            title='Print'
          >
            <PrinterIcon className='h-4 w-4' />
          </button>
          <button
            onClick={() => handleRestore(item)}
            className="text-purple-600 hover:bg-purple-100 p-1.5 rounded-full transition-all duration-200 hover:scale-110">
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {isPrintModalOpen && (
        <LegislationFileViewer
          selectedLegislation={selectedItem}
          onClose={() => setIsPrintModalOpen(false)}
          iframePrintLegislation={iframePrintLegislation}
        />
      )}

      <Table
        header={header}
        columns={columns}
        data={tableData}
        getRowKey={item => item.id}
        pagination={{
          currentPage: pagination.currentPage,
          perPage: pagination.perPage,
          lastPage: historyData?.data?.meta?.last_page || 1,
          total: historyData?.data?.meta?.total || 0,
        }}
        onPageChange={handlePageChange}
        onPerPageChange={value => setPagination(prev => ({ ...prev, perPage: value, currentPage: 1 }))}
        isLoading={isLoading}
      />

      <ViewLegislationModal
        isOpen={openViewModal}
        onClose={() => {
          setOpenViewModal(false);
          setSelectedItem(null);
        }}
        legislation={selectedItem}
        type="history"
      />

      {restoreModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Confirm Restore</h2>
            <p className="mb-6">Restore legislation <b>{restoreItem?.legislation_number}</b>?</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => { setRestoreModalOpen(false); setRestoreItem(null); }} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={confirmRestore} className="px-4 py-2 bg-red-500 text-white rounded">Restore</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryArchivesTable;
